import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Wallet, QrCode, Send, Plus, Copy, Check, Scan, X, Gift, TrendingUp, ArrowDownRight, ArrowUpRight, Clock, CheckCircle, XCircle, History, Camera, User, Phone } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { fmtDateTime, fmtDateHuman } from '../../utils/dateFmt';
import jsQR from 'jsqr';
import { copyToClipboard } from '../../utils/clipboard';
import { formatIST } from '../../utils/timezone';

const Wallets = () => {
  const { user, fetchUser } = useAuth();
  const [balances, setBalances] = useState(null);
  const [myQR, setMyQR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddFunds, setShowAddFunds] = useState(false);
  const [showScanPay, setShowScanPay] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showMyQR, setShowMyQR] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [addAmount, setAddAmount] = useState('');
  const [scanPayData, setScanPayData] = useState({ upi_id: '', amount: '', note: '' });
  const [transferData, setTransferData] = useState({ username: '', amount: '', wallet_type: 'cash_wallet' });
  const [recipientInfo, setRecipientInfo] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [settings, setSettings] = useState(null);
  const [fundRequests, setFundRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [atOpen, setATOpen] = useState(false);
  const [atAction, setATAction] = useState('send'); // send | deposit | withdraw | ledger
  const [offlineQueue, setOfflineQueue] = useState(() => readOfflineQueue());
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [drainingQueue, setDrainingQueue] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    fetchWalletData();
    fetchFundRequests();
  }, []);

  // 📡 OFFLINE TRANSFER QUEUE — detect online/offline, auto-drain on reconnect
  const drainOfflineQueue = useCallback(async () => {
    const q = readOfflineQueue();
    if (!q.length) return;
    setDrainingQueue(true);
    const remaining = [];
    let sent = 0, failed = 0;
    for (const item of q) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await api.post('/at-wallet/send', {
          to_user_id: item.to_user_id, to_username: item.to_username,
          to_mobile: item.to_mobile, amount: item.amount, note: item.note,
        });
        sent++;
      } catch (e) {
        // Keep failed items in queue; skip idempotency duplicates
        const msg = e?.response?.data?.detail || '';
        if (!/insufficient|not found|yourself/i.test(msg)) remaining.push(item);
        failed++;
      }
      // 1s gap between sends to avoid rate spikes
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 1000));
    }
    writeOfflineQueue(remaining);
    setOfflineQueue(remaining);
    setDrainingQueue(false);
    if (sent > 0) toast.success(`✅ ${sent} offline transfer(s) synced`);
    if (failed > 0 && remaining.length === 0) toast.error(`${failed} offline transfer(s) dropped (recipient/balance issue)`);
    fetchWalletData();
  }, []);

  useEffect(() => {
    const handleOnline = () => { setIsOnline(true); drainOfflineQueue(); };
    const handleOffline = () => { setIsOnline(false); toast('📡 Offline mode — transfers will queue', { icon: '📴' }); };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    // Try draining on mount if we have pending items and are online
    if (navigator.onLine && readOfflineQueue().length > 0) drainOfflineQueue();
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [drainOfflineQueue]);

  // 🔊 Soundbox for RECEIVER — poll for new incoming AT and announce via TTS (0-cost).
  // Runs every 15s while Wallets page is open. Stores lastSeen id per-user in localStorage.
  useEffect(() => {
    const KEY = 'at_last_seen_tx_id';
    const tick = async () => {
      try {
        const r = await api.get('/at-wallet/ledger?limit=5');
        const rows = r.data?.ledger || [];
        if (!rows.length) return;
        const lastSeen = localStorage.getItem(KEY);
        // First run just primes — don't announce past transactions
        if (!lastSeen) { localStorage.setItem(KEY, rows[0].id); return; }
        // Collect fresh incoming credits since lastSeen
        const fresh = [];
        for (const tx of rows) {
          if (tx.id === lastSeen) break;
          if (tx.direction === 'credit' && (tx.tx_type || '').toLowerCase().includes('transfer')) fresh.push(tx);
        }
        if (fresh.length && rows[0].id !== lastSeen) {
          fresh.forEach((tx) => {
            const from = tx.description?.match(/from\s+@?(\w+)/i)?.[1] || 'someone';
            speakSoundbox(`${tx.amount} AT received from ${from}. New balance ${tx.balance_after} AT.`);
          });
          localStorage.setItem(KEY, rows[0].id);
          fetchWalletData();  // refresh balance display
        } else if (rows[0].id !== lastSeen) {
          localStorage.setItem(KEY, rows[0].id);
        }
      } catch { /* silent */ }
    };
    tick();
    const iv = setInterval(tick, 15000);
    return () => clearInterval(iv);
  }, []);

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      const [balancesRes, qrRes, settingsRes] = await Promise.all([
        api.get('/wallet/balances'),
        api.get('/wallet/my-qr'),
        api.get('/settings')
      ]);
      setBalances(balancesRes.data);
      setMyQR(qrRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to fetch wallet data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFundRequests = async () => {
    try {
      setLoadingRequests(true);
      const res = await api.get('/wallet/my-fund-requests');
      setFundRequests(res.data || []);
    } catch (error) {
      console.error('Failed to fetch fund requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  };

  const copyUPI = async (upi) => {
    const success = await copyToClipboard(upi);
    if (success) {
      setCopied(true);
      toast.success('UPI ID copied!');
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('Failed to copy');
    }
  };

  const handleAddFunds = async () => {
    const amount = parseFloat(addAmount);
    if (!amount || amount <= 0) {
      toast.error('Enter valid amount');
      return;
    }
    
    if (amount < 100) {
      toast.error('Minimum amount is ₹100');
      return;
    }
    
    if (amount > 1000000) {
      toast.error('Maximum amount is ₹10,00,000');
      return;
    }

    setProcessing(true);
    try {
      await api.post('/wallet/add-funds', { amount });
      toast.success('Fund request submitted! Complete payment and wait for approval.');
      setShowAddFunds(false);
      setAddAmount('');
      fetchFundRequests(); // Refresh fund requests list
      
      // Play notification sound
      try {
        const audio = new Audio('/notification.mp3');
        audio.play().catch(() => {});
      } catch (e) {}
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setProcessing(false);
    }
  };

  const lookupRecipient = async () => {
    if (!scanPayData.upi_id.trim()) return;
    
    try {
      const res = await api.get(`/wallet/lookup/${encodeURIComponent(scanPayData.upi_id)}`);
      setRecipientInfo(res.data);
    } catch (error) {
      setRecipientInfo(null);
      toast.error('User not found. Try username, mobile number, or UPI ID.');
    }
  };

  // QR Scanner functions
  const startScanner = async () => {
    try {
      setShowScanner(true);
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      // Start scanning after video is playing
      requestAnimationFrame(scanQRCode);
    } catch (error) {
      console.error('Camera error:', error);
      setShowScanner(false);
      if (error.name === 'NotAllowedError') {
        toast.error('Camera permission denied. Please allow camera access in your browser settings.');
      } else if (error.name === 'NotFoundError') {
        toast.error('No camera found on this device.');
      } else {
        toast.error('Could not access camera. Enter details manually.');
      }
    }
  };

  const stopScanner = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setShowScanner(false);
  };

  const scanQRCode = () => {
    if (!videoRef.current || !streamRef.current) return;
    
    const video = videoRef.current;
    
    if (video.readyState === video.HAVE_ENOUGH_DATA && video.videoWidth > 0) {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      // Try to detect QR code using BarcodeDetector if available
      if ('BarcodeDetector' in window) {
        const barcodeDetector = new window.BarcodeDetector({ formats: ['qr_code'] });
        barcodeDetector.detect(canvas)
          .then(barcodes => {
            if (barcodes.length > 0) {
              const qrData = barcodes[0].rawValue;
              handleQRResult(qrData);
            } else if (streamRef.current) {
              requestAnimationFrame(scanQRCode);
            }
          })
          .catch(() => {
            if (streamRef.current) requestAnimationFrame(scanQRCode);
          });
      } else {
        // Fallback to jsQR for browsers without BarcodeDetector (Safari, Firefox)
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        
        if (code && code.data) {
          handleQRResult(code.data);
        } else if (streamRef.current) {
          requestAnimationFrame(scanQRCode);
        }
      }
    } else {
      setTimeout(scanQRCode, 500);
    }
  };

  const handleQRResult = (data) => {
    stopScanner();
    
    // Extract UPI ID or username from QR data
    let identifier = data;
    
    // Our format: anantpay://anant.username@wallet
    if (data.startsWith('anantpay://')) {
      identifier = data.replace('anantpay://', '');
    }
    // UPI format: upi://pay?pa=xxx@xxx&pn=Name
    else if (data.includes('upi://pay')) {
      const match = data.match(/pa=([^&]+)/);
      if (match) identifier = match[1];
    }
    
    // Extract username from anant.username@wallet format
    if (identifier.startsWith('anant.') && identifier.endsWith('@wallet')) {
      // Keep the full UPI ID for lookup
    }
    
    setScanPayData({ ...scanPayData, upi_id: identifier });
    toast.success('QR Code scanned!');
    
    // Auto lookup
    setTimeout(async () => {
      try {
        const res = await api.get(`/wallet/lookup/${encodeURIComponent(identifier)}`);
        setRecipientInfo(res.data);
      } catch (error) {
        toast.error('Could not find user from QR code');
      }
    }, 100);
  };

  const handleScanPay = async () => {
    const amount = parseFloat(scanPayData.amount);
    if (!scanPayData.upi_id.trim() && !recipientInfo) {
      toast.error('Enter recipient username, mobile, or UPI ID');
      return;
    }
    if (!amount || amount <= 0) {
      toast.error('Enter valid amount');
      return;
    }

    setProcessing(true);
    try {
      // Use recipientInfo.username if available (from lookup), otherwise use the input
      const recipientId = recipientInfo?.username || scanPayData.upi_id;
      
      const res = await api.post('/wallet/scan-pay', {
        recipient_upi_id: recipientId,
        amount: amount,
        note: scanPayData.note
      });
      toast.success(res.data.message);
      setShowScanPay(false);
      setScanPayData({ upi_id: '', amount: '', note: '' });
      setRecipientInfo(null);
      fetchWalletData();
      if (fetchUser) fetchUser();
      
      // Play sound
      try {
        const audio = new Audio('/notification.mp3');
        audio.play().catch(() => {});
      } catch (e) {}
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleTransfer = async () => {
    const amount = parseFloat(transferData.amount);
    if (!transferData.username.trim()) {
      toast.error('Enter recipient username');
      return;
    }
    if (!amount || amount <= 0) {
      toast.error('Enter valid amount');
      return;
    }

    setProcessing(true);
    try {
      await api.post('/wallet/transfer', {
        recipient_username: transferData.username,
        amount: amount,
        wallet_type: transferData.wallet_type
      });
      toast.success(`₹${amount} transferred successfully!`);
      setShowTransfer(false);
      setTransferData({ username: '', amount: '', wallet_type: 'cash_wallet' });
      fetchWalletData();
      if (fetchUser) fetchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-xl">Loading wallets...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="wallets-page">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-bold mb-2">My Wallets</h1>
        <p className="text-muted-foreground mb-4">Manage your 3 wallets - Cash, Commission & Promotion</p>

        {/* 📡 OFFLINE STATUS + QUEUE BANNER */}
        {(!isOnline || offlineQueue.length > 0) && (
          <div className={`mb-4 p-3 rounded-xl border-2 flex items-center gap-3 ${isOnline ? 'bg-cyan-950/40 border-cyan-500/50' : 'bg-amber-950/40 border-amber-500/60'}`}
               data-testid="offline-queue-banner">
            <div className="text-2xl">{isOnline ? (drainingQueue ? '🔄' : '📤') : '📴'}</div>
            <div className="flex-1 text-sm">
              {!isOnline && <div className="font-black text-amber-300">Offline mode — transfers queued locally</div>}
              {isOnline && offlineQueue.length > 0 && (
                <div className="font-black text-cyan-300">{drainingQueue ? 'Syncing' : 'Pending'} {offlineQueue.length} offline transfer(s)...</div>
              )}
              <div className="text-[10px] opacity-70">Queue auto-syncs when network comes back</div>
            </div>
            <div className="text-xs font-black px-2 py-1 rounded-full bg-white/10">{offlineQueue.length}</div>
          </div>
        )}

        {/* Total Balance */}
        <div className="glass-card p-6 mb-8 text-center bg-gradient-to-r from-primary/20 to-accent/20">
          <p className="text-lg text-muted-foreground mb-2">Total Balance</p>
          <p className="text-5xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            ₹{balances?.total_balance?.toFixed(2) || '0.00'}
          </p>
        </div>

        {/* 4 Wallet Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* AT Pay Wallet */}
          <div className="glass-card p-6 border-2 border-emerald-500/40" data-testid="at-wallet-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500/30 to-cyan-500/30 rounded-xl flex items-center justify-center">
                <span className="text-3xl">💎</span>
              </div>
              <div>
                <h3 className="text-xl font-bold">AT Pay Wallet</h3>
                <p className="text-xs text-muted-foreground">Anant Token · full network utility</p>
              </div>
            </div>
            <p className="text-4xl font-bold text-emerald-400 mb-3" data-testid="at-wallet-balance">
              {(balances?.at_balance ?? 0).toLocaleString('en-IN')} AT
            </p>
          <div className="grid grid-cols-3 gap-2 mb-2">
              <button onClick={() => { setATAction('send'); setATOpen(true); }} data-testid="at-send-btn" className="px-2 py-2 rounded-lg bg-emerald-500 text-black text-xs font-black">↗ Send</button>
              <button onClick={() => { setATAction('scan'); setATOpen(true); }} data-testid="at-scan-btn" className="px-2 py-2 rounded-lg bg-fuchsia-500 text-white text-xs font-black">📷 Scan Pay</button>
              <button onClick={() => { setATAction('qr'); setATOpen(true); }} data-testid="at-qr-btn" className="px-2 py-2 rounded-lg bg-slate-800 text-white text-xs font-black">📱 My QR</button>
              <button onClick={() => { setATAction('deposit'); setATOpen(true); }} data-testid="at-deposit-btn" className="px-2 py-2 rounded-lg bg-cyan-500 text-black text-xs font-black">➕ Deposit</button>
              <button onClick={() => { setATAction('withdraw'); setATOpen(true); }} data-testid="at-withdraw-btn" className="px-2 py-2 rounded-lg bg-amber-500 text-black text-xs font-black">💸 Withdraw</button>
              <button onClick={() => { setATAction('ledger'); setATOpen(true); }} data-testid="at-ledger-btn" className="px-2 py-2 rounded-lg bg-slate-700 text-white text-xs font-black">📜 Ledger</button>
            </div>
            <div className="bg-emerald-500/10 rounded-lg p-2">
              <p className="text-[10px] text-emerald-300">Send / Deposit / Withdraw / History — full wallet controls</p>
            </div>
          </div>

          {/* Cash Wallet */}
          <div className="glass-card p-6 border-2 border-green-500/30" data-testid="cash-wallet-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 bg-green-500/20 rounded-xl flex items-center justify-center">
                <Wallet className="w-7 h-7 text-green-500" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Cash Wallet</h3>
                <p className="text-xs text-muted-foreground">For purchases & transfers</p>
              </div>
            </div>
            <p className="text-4xl font-bold text-green-500 mb-4">
              ₹{balances?.cash_wallet?.toFixed(2) || '0.00'}
            </p>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setShowAddFunds(true)}
                className="flex-1 btn-primary text-sm py-2 flex items-center justify-center gap-1"
              >
                <Plus className="w-4 h-4" /> Add
              </button>
              <button
                onClick={() => setShowScanPay(true)}
                className="flex-1 px-3 py-2 bg-green-500/20 text-green-500 rounded-full text-sm flex items-center justify-center gap-1"
              >
                <Scan className="w-4 h-4" /> Pay
              </button>
              <button
                onClick={() => setShowMyQR(true)}
                className="flex-1 px-3 py-2 bg-white/10 rounded-full text-sm flex items-center justify-center gap-1"
              >
                <QrCode className="w-4 h-4" /> QR
              </button>
            </div>
          </div>

          {/* Commission Wallet */}
          <div className="glass-card p-6 border-2 border-blue-500/30" data-testid="commission-wallet-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 bg-blue-500/20 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-7 h-7 text-blue-500" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Commission Wallet</h3>
                <p className="text-xs text-muted-foreground">Affiliate Network Level Earnings (33%)</p>
              </div>
            </div>
            <p className="text-4xl font-bold text-blue-500 mb-4">
              ₹{balances?.commission_wallet?.toFixed(2) || '0.00'}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setTransferData({ ...transferData, wallet_type: 'commission_wallet' });
                  setShowTransfer(true);
                }}
                className="flex-1 px-3 py-2 bg-blue-500/20 text-blue-500 rounded-full text-sm flex items-center justify-center gap-1"
              >
                <Send className="w-4 h-4" /> Transfer
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Use for 50% purchases or member transfers
            </p>
          </div>

          {/* Promotion Wallet */}
          <div className="glass-card p-6 border-2 border-purple-500/30" data-testid="promotion-wallet-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <Gift className="w-7 h-7 text-purple-500" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Promotion Wallet</h3>
                <p className="text-xs text-muted-foreground">Bonus Earnings (7%)</p>
              </div>
            </div>
            <p className="text-4xl font-bold text-purple-500 mb-4">
              ₹{balances?.promotion_wallet?.toFixed(2) || '0.00'}
            </p>
            <div className="bg-purple-500/10 rounded-lg p-3">
              <p className="text-xs text-purple-400">
                7% of your direct referrals' purchases credited here automatically
              </p>
            </div>
          </div>
        </div>

        {/* My Wallet QR Code */}
        <div className="glass-card p-6 mb-8">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <QrCode className="w-5 h-5 text-primary" />
            My Wallet QR Code
          </h2>
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="bg-white p-4 rounded-xl">
              {myQR?.qr_code && (
                <img src={myQR.qr_code} alt="My Wallet QR" className="w-48 h-48" data-testid="my-qr-image" />
              )}
            </div>
            <div className="flex-1">
              <p className="text-muted-foreground mb-2">Your Wallet UPI ID</p>
              <div className="flex items-center gap-2 bg-white/10 rounded-lg p-3 mb-4">
                <p className="font-mono text-lg font-bold text-primary flex-1" data-testid="my-upi-id">
                  {myQR?.upi_id || balances?.wallet_upi_id}
                </p>
                <button onClick={() => copyUPI(myQR?.upi_id || balances?.wallet_upi_id)} className="p-2 hover:bg-white/10 rounded-lg">
                  {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-sm text-muted-foreground">
                Share this QR or UPI ID to receive payments directly to your Cash Wallet
              </p>
            </div>
          </div>
        </div>

        {/* Commission Structure Info */}
        <div className="glass-card p-6">
          <h2 className="text-xl font-bold mb-4">Wallet Distribution (40% Total)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
              <h3 className="font-bold text-blue-500 mb-2">Commission Wallet - 33%</h3>
              <p className="text-sm text-muted-foreground">
                L1: 10% | L2: 7% | L3: 5% | L4: 4% | L5: 3% | L6: 2% | L7: 2%
              </p>
            </div>
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
              <h3 className="font-bold text-purple-500 mb-2">Promotion Wallet - 7%</h3>
              <p className="text-sm text-muted-foreground">
                7% of every direct referral's purchase goes to your Promotion Wallet
              </p>
            </div>
          </div>
        </div>

        {/* Fund Request History */}
        <div className="glass-card p-6" data-testid="fund-request-history">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <History className="w-5 h-5 text-primary" />
            Fund Request History
          </h2>
          
          {loadingRequests ? (
            <div className="text-center py-4 text-muted-foreground">Loading requests...</div>
          ) : fundRequests.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No fund requests yet</p>
              <p className="text-sm mt-2">Click "Add" on your Cash Wallet to submit a request</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {fundRequests.map((req) => (
                <div 
                  key={req.id} 
                  className={`p-4 rounded-xl border ${
                    req.status === 'approved' ? 'bg-green-500/10 border-green-500/30' :
                    req.status === 'rejected' ? 'bg-red-500/10 border-red-500/30' :
                    'bg-yellow-500/10 border-yellow-500/30'
                  }`}
                  data-testid={`fund-request-${req.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {req.status === 'approved' ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : req.status === 'rejected' ? (
                        <XCircle className="w-5 h-5 text-red-500" />
                      ) : (
                        <Clock className="w-5 h-5 text-yellow-500" />
                      )}
                      <div>
                        <p className="font-bold text-lg">₹{req.amount?.toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatIST(req.created_at)} IST
                        </p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      req.status === 'approved' ? 'bg-green-500/20 text-green-500' :
                      req.status === 'rejected' ? 'bg-red-500/20 text-red-500' :
                      'bg-yellow-500/20 text-yellow-500'
                    }`}>
                      {req.status?.charAt(0).toUpperCase() + req.status?.slice(1)}
                    </span>
                  </div>
                  {req.admin_note && (
                    <p className="mt-2 text-sm text-muted-foreground border-t border-white/10 pt-2">
                      Note: {req.admin_note}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add Funds Modal */}
        {showAddFunds && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <div className="glass-card p-8 max-w-md w-full" data-testid="add-funds-modal">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Add Funds</h2>
                <button onClick={() => setShowAddFunds(false)} className="p-2 hover:bg-white/10 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Amount (₹)</label>
                <input
                  type="number"
                  value={addAmount}
                  onChange={(e) => setAddAmount(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-2xl font-bold"
                  placeholder="Min ₹100 - Max ₹10,00,000"
                  min="100"
                  max="1000000"
                  data-testid="add-funds-amount-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Minimum: ₹100 | Maximum: ₹10,00,000</p>
              </div>

              {/* Payment QR or UPI Info */}
              {(settings?.manual_payment_qr || settings?.manual_payment_upi) ? (
                <div className="mb-6 p-4 bg-white/5 rounded-xl" data-testid="admin-payment-info">
                  <p className="text-sm font-semibold mb-3 text-center">Pay Admin to Add Funds</p>
                  {settings?.manual_payment_qr && (
                    <div className="flex justify-center mb-3">
                      <img src={settings.manual_payment_qr} alt="Payment QR" className="w-40 h-40 rounded-lg bg-white p-2" data-testid="admin-payment-qr" />
                    </div>
                  )}
                  {settings?.manual_payment_upi && (
                    <div className="bg-white/10 rounded-lg p-3 text-center">
                      <p className="text-xs text-muted-foreground mb-1">UPI ID</p>
                      <p className="font-mono font-bold text-primary" data-testid="admin-payment-upi">{settings.manual_payment_upi}</p>
                      <button 
                        onClick={() => copyUPI(settings.manual_payment_upi)}
                        className="mt-2 text-xs text-primary hover:underline flex items-center gap-1 mx-auto"
                      >
                        <Copy className="w-3 h-3" /> Copy UPI ID
                      </button>
                    </div>
                  )}
                  <p className="text-xs text-center text-muted-foreground mt-3">
                    After payment, submit the request. Admin will verify and credit your wallet.
                  </p>
                </div>
              ) : (
                <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl text-center">
                  <p className="text-sm text-yellow-500">Payment details not configured yet.</p>
                  <p className="text-xs text-muted-foreground mt-1">Please contact admin for payment information.</p>
                </div>
              )}

              <button
                onClick={handleAddFunds}
                disabled={processing}
                className="w-full btn-primary"
              >
                {processing ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        )}

        {/* Scan & Pay Modal */}
        {showScanPay && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <div className="glass-card p-8 max-w-md w-full" data-testid="scan-pay-modal">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <Scan className="w-6 h-6 text-green-500" /> Scan & Pay
                </h2>
                <button onClick={() => { setShowScanPay(false); setRecipientInfo(null); stopScanner(); }} className="p-2 hover:bg-white/10 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* QR Scanner Section */}
              {showScanner ? (
                <div className="mb-6">
                  <div className="relative aspect-square rounded-xl overflow-hidden bg-black mb-4">
                    <video 
                      ref={videoRef} 
                      className="w-full h-full object-cover"
                      playsInline
                      muted
                    />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-48 h-48 border-2 border-green-500 rounded-2xl"></div>
                    </div>
                    <p className="absolute bottom-4 left-0 right-0 text-center text-sm text-white/70">
                      Point camera at QR code
                    </p>
                  </div>
                  <button
                    onClick={stopScanner}
                    className="w-full py-3 bg-red-500/20 text-red-400 rounded-xl flex items-center justify-center gap-2"
                  >
                    <X className="w-5 h-5" /> Cancel Scan
                  </button>
                </div>
              ) : (
                <button
                  onClick={startScanner}
                  className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl mb-6 flex items-center justify-center gap-3 font-semibold"
                  data-testid="open-camera-btn"
                >
                  <Camera className="w-6 h-6" />
                  Open Camera to Scan QR
                </button>
              )}

              <div className="relative mb-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/20"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="px-4 bg-background text-muted-foreground text-sm">OR enter manually</span>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                    <User className="w-4 h-4" /> Username / Mobile No. / UPI ID
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={scanPayData.upi_id}
                      onChange={(e) => setScanPayData({ ...scanPayData, upi_id: e.target.value })}
                      className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-3"
                      placeholder="username OR 9876543210 OR anant.user@wallet"
                    />
                    <button
                      onClick={lookupRecipient}
                      className="px-4 bg-primary/20 text-primary rounded-lg"
                    >
                      Find
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Enter username, mobile number, or UPI ID
                  </p>
                </div>

                {recipientInfo && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <p className="text-sm text-green-500 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Paying to: <strong>{recipientInfo.name}</strong> (@{recipientInfo.username})
                    </p>
                    {recipientInfo.mobile && (
                      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                        <Phone className="w-3 h-3" /> Mobile: ****{recipientInfo.mobile}
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-2">Amount (₹)</label>
                  <input
                    type="number"
                    value={scanPayData.amount}
                    onChange={(e) => setScanPayData({ ...scanPayData, amount: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-xl font-bold"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Note (Optional)</label>
                  <input
                    type="text"
                    value={scanPayData.note}
                    onChange={(e) => setScanPayData({ ...scanPayData, note: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3"
                    placeholder="What's this for?"
                  />
                </div>
              </div>

              <p className="text-sm text-muted-foreground mb-4">
                Balance: <span className="text-green-500 font-bold">₹{balances?.cash_wallet?.toFixed(2)}</span>
              </p>

              <button
                onClick={handleScanPay}
                disabled={processing || !recipientInfo}
                className="w-full btn-primary disabled:opacity-50"
              >
                {processing ? 'Processing...' : `Pay ₹${scanPayData.amount || '0'}`}
              </button>
            </div>
          </div>
        )}

        {/* Transfer Modal */}
        {showTransfer && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <div className="glass-card p-8 max-w-md w-full" data-testid="transfer-modal">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Transfer Funds</h2>
                <button onClick={() => setShowTransfer(false)} className="p-2 hover:bg-white/10 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium mb-2">From Wallet</label>
                  <select
                    value={transferData.wallet_type}
                    onChange={(e) => setTransferData({ ...transferData, wallet_type: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3"
                  >
                    <option value="cash_wallet">Cash Wallet (₹{balances?.cash_wallet?.toFixed(2)})</option>
                    <option value="commission_wallet">Commission Wallet (₹{balances?.commission_wallet?.toFixed(2)})</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Recipient (Username / Mobile / Email / UPI)</label>
                  <input
                    type="text"
                    value={transferData.username}
                    onChange={(e) => setTransferData({ ...transferData, username: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3"
                    placeholder="Enter username, mobile, email or UPI ID"
                  />
                  <p className="text-xs text-muted-foreground mt-1">e.g. admin, 9876543210, user@email.com</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Amount (₹)</label>
                  <input
                    type="number"
                    value={transferData.amount}
                    onChange={(e) => setTransferData({ ...transferData, amount: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-xl font-bold"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <button
                onClick={handleTransfer}
                disabled={processing}
                className="w-full btn-primary"
              >
                {processing ? 'Transferring...' : 'Transfer'}
              </button>
            </div>
          </div>
        )}

        {/* My QR Full Screen Modal */}
        {showMyQR && (
          <div className="fixed inset-0 bg-background/95 z-50 flex items-center justify-center p-6">
            <div className="text-center">
              <button onClick={() => setShowMyQR(false)} className="absolute top-4 right-4 p-3 bg-white/10 rounded-full">
                <X className="w-6 h-6" />
              </button>
              <p className="text-xl mb-4">Scan to Pay Me</p>
              <div className="bg-white p-6 rounded-2xl inline-block mb-4">
                {myQR?.qr_code && (
                  <img src={myQR.qr_code} alt="My QR" className="w-64 h-64" />
                )}
              </div>
              <p className="text-2xl font-bold">{myQR?.name}</p>
              <p className="text-lg font-mono text-primary">{myQR?.upi_id}</p>
            </div>
          </div>
        )}
      </div>
      {atOpen && <ATWalletModal action={atAction} onClose={() => setATOpen(false)} onReload={fetchWalletData} isOnline={isOnline} setOfflineQueue={setOfflineQueue} />}
    </div>
  );
};

// ============ OFFLINE QUEUE HELPERS ============
const OFFLINE_KEY = 'at_offline_queue_v1';
function readOfflineQueue() {
  try { return JSON.parse(localStorage.getItem(OFFLINE_KEY) || '[]'); } catch { return []; }
}
function writeOfflineQueue(q) {
  try { localStorage.setItem(OFFLINE_KEY, JSON.stringify(q)); } catch { /* noop */ }
}
function pushOfflineTransfer(payload) {
  const q = readOfflineQueue();
  q.push({ ...payload, queued_at: new Date().toISOString(), local_id: Math.random().toString(36).slice(2) });
  writeOfflineQueue(q);
  return q;
}

// ============ AT WALLET ACTION MODAL ============

// Free Soundbox — announces AT transfer details via browser TTS (0-cost). hi-IN voice preferred.
function speakSoundbox(text) {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
  const u = new SpeechSynthesisUtterance(text);
  u.lang = 'hi-IN'; u.rate = 0.95; u.pitch = 1.0; u.volume = 1.0;
  const voices = window.speechSynthesis.getVoices?.() || [];
  const hi = voices.find((v) => v.lang?.startsWith('hi')) || voices.find((v) => /hindi|india/i.test(v.name || ''));
  if (hi) u.voice = hi;
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(u);
}


function ATWalletModal({ action, onClose, onReload, isOnline = true, setOfflineQueue }) {
  const [busy, setBusy] = useState(false);
  const [sendForm, setSendForm] = useState({ query: '', to_user_id: '', to_username: '', to_mobile: '', to_name: '', amount: '', note: '' });
  const [suggestions, setSuggestions] = useState([]);
  const [withdrawForm, setWithdrawForm] = useState({ amount: '', method: 'upi', upi_id: '', bank_account: '', bank_ifsc: '', bank_name: '' });
  const [ledger, setLedger] = useState([]);
  const [qr, setQr] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scannedPayee, setScannedPayee] = useState(null);
  const videoRefLocal = useRef(null);
  const streamRefLocal = useRef(null);

  useEffect(() => {
    if (action === 'ledger') api.get('/at-wallet/ledger?limit=100').then((r) => setLedger(r.data?.ledger || []));
    if (action === 'qr') api.get('/at-wallet/qr').then((r) => setQr(r.data));
  }, [action]);

  // Live lookup for send
  useEffect(() => {
    if (action !== 'send' || !sendForm.query || sendForm.query.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try { const r = await api.get('/at-wallet/lookup', { params: { q: sendForm.query } }); setSuggestions(r.data?.results || []); }
      catch { setSuggestions([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [sendForm.query, action]);

  const pickRecipient = (r) => {
    setSendForm({ ...sendForm, to_user_id: r.id, to_username: r.username, to_mobile: r.mobile || '', to_name: r.name || r.username, query: r.name || r.username });
    setSuggestions([]);
  };
  const send = async () => {
    const amt = parseFloat(sendForm.amount);
    if (!amt || !sendForm.to_user_id) { toast.error('Pick recipient + amount'); return; }
    // 📡 OFFLINE — queue and exit
    if (!isOnline || !navigator.onLine) {
      const q = pushOfflineTransfer({
        to_user_id: sendForm.to_user_id, to_username: sendForm.to_username,
        to_mobile: sendForm.to_mobile, to_name: sendForm.to_name,
        amount: amt, note: sendForm.note,
      });
      if (setOfflineQueue) setOfflineQueue(q);
      toast.success(`📴 Queued for @${sendForm.to_username} · will sync when online`);
      onClose();
      return;
    }
    setBusy(true);
    try {
      const r = await api.post('/at-wallet/send', {
        to_user_id: sendForm.to_user_id,
        amount: amt, note: sendForm.note,
      });
      const recipient = sendForm.to_name || r.data.to || 'user';
      const newBal = r.data.balance !== undefined ? ` New balance ${r.data.balance} AT.` : '';
      // 🔊 Soundbox — sender announcement
      speakSoundbox(`${amt} AT sent to ${recipient}.${newBal}`);
      toast.success(`✓ Sent ${amt} AT to @${r.data.to}`); onReload(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const withdraw = async () => {
    if (!withdrawForm.amount) { toast.error('Amount required'); return; }
    setBusy(true);
    try { await api.post('/at-wallet/withdraw', { ...withdrawForm, amount: parseFloat(withdrawForm.amount) }); toast.success('✓ Withdraw request submitted'); onReload(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const goDeposit = () => { window.location.href = '/at-deposit'; };

  // ============ SCAN PAY ============
  const scanActiveRef = useRef(false);
  const startScan = async () => {
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      toast.error('Camera needs a secure (https) connection. Open the app over https and try again.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
      streamRefLocal.current = stream;
      // <video> mounts on this render; the effect below attaches the stream and
      // the video's onLoadedMetadata/onCanPlay starts the decode loop.
      setScanning(true);
    } catch (err) {
      console.error('AT scan camera error:', err);
      setScanning(false);
      const name = err?.name;
      if (name === 'NotAllowedError' || name === 'SecurityError') toast.error('Camera permission denied. Allow camera access in your browser settings and retry.');
      else if (name === 'NotFoundError' || name === 'OverconstrainedError') toast.error('No camera found on this device. Use Send AT by username instead.');
      else if (name === 'NotReadableError') toast.error('Camera is in use by another app. Close it and retry.');
      else toast.error(`Could not start camera${name ? ` (${name})` : ''}. Use Send AT by username instead.`);
    }
  };
  useEffect(() => {
    const v = videoRefLocal.current;
    if (!scanning || !v || !streamRefLocal.current) return;
    v.srcObject = streamRefLocal.current;
    v.play().catch(() => { /* onCanPlay retries the loop start */ });
  }, [scanning]);
  const stopScan = () => {
    scanActiveRef.current = false;
    setScanning(false);
    try { streamRefLocal.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    streamRefLocal.current = null;
    if (videoRefLocal.current) videoRefLocal.current.srcObject = null;
  };
  // Started from the video's onLoadedMetadata/onCanPlay; idempotent.
  const scanLoop = () => {
    if (scanActiveRef.current) return;
    scanActiveRef.current = true;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    // Prefer native BarcodeDetector (Chrome Android/macOS); fall back to jsQR (Safari, Firefox, Windows).
    let detector = null;
    if ('BarcodeDetector' in window) {
      try { detector = new window.BarcodeDetector({ formats: ['qr_code'] }); } catch { detector = null; }
    }
    const step = async () => {
      const v = videoRefLocal.current;
      if (!scanActiveRef.current || !v || !streamRefLocal.current) { scanActiveRef.current = false; return; }
      if (v.readyState >= 2 && v.videoWidth > 0) {
        let data = null;
        if (detector) {
          try { const codes = await detector.detect(v); if (codes.length) data = codes[0].rawValue; }
          catch { detector = null; } // detector unusable here → jsQR from now on
        } else {
          // Downscale for jsQR so each frame decodes fast enough
          const scale = Math.min(1, 640 / v.videoWidth);
          canvas.width = Math.round(v.videoWidth * scale); canvas.height = Math.round(v.videoHeight * scale);
          ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
          const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(img.data, img.width, img.height);
          if (code?.data) data = code.data;
        }
        if (data && scanActiveRef.current) { scanActiveRef.current = false; handleScanned(data); return; }
      }
      if (scanActiveRef.current) requestAnimationFrame(step);
    };
    step();
  };
  const handleScanned = (data) => {
    stopScan();
    try {
      let payload;
      if (data.startsWith('anant://at-pay')) {
        const url = new URL(data.replace('anant://', 'https://x/'));
        payload = { to_user_id: url.searchParams.get('id'), to_username: url.searchParams.get('u') };
      } else {
        payload = JSON.parse(data);
      }
      if (!payload || payload.type !== 'at_wallet' && !payload.to_user_id) {
        toast.error('Not an Anant AT Wallet QR'); return;
      }
      setScannedPayee(payload);
      toast.success(`Scanned: @${payload.to_username || payload.to_user_id}`);
    } catch { toast.error('QR unreadable'); }
  };
  useEffect(() => () => stopScan(), []); // eslint-disable-line

  const payScanned = async () => {
    if (!scannedPayee) return;
    const amt = parseFloat(sendForm.amount);
    if (!amt) { toast.error('Amount required'); return; }
    setBusy(true);
    try {
      const r = await api.post('/at-wallet/send', {
        to_user_id: scannedPayee.to_user_id,
        to_username: scannedPayee.to_username,
        amount: amt, note: sendForm.note,
      });
      toast.success(`✓ Paid ${amt} AT to @${r.data.to}`); onReload(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-3 overflow-y-auto" onClick={onClose} data-testid="at-modal">
      <div className="bg-slate-900 border-2 border-emerald-400 rounded-2xl p-4 max-w-md w-full text-white" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-black text-emerald-300 uppercase tracking-widest">
            {action === 'send' ? '↗ Send AT' : action === 'scan' ? '📷 Scan & Pay' : action === 'qr' ? '📱 My AT Wallet QR' : action === 'deposit' ? '➕ Deposit AT' : action === 'withdraw' ? '💸 Withdraw AT' : '📜 AT Ledger'}
          </div>
          <button onClick={onClose} className="text-slate-400 text-lg">✕</button>
        </div>

        {action === 'send' && (
          <div className="space-y-2">
            <div className="relative">
              <input placeholder="Search name / @username / mobile" value={sendForm.query}
                     onChange={(e) => setSendForm({ ...sendForm, query: e.target.value, to_user_id: '', to_username: '', to_mobile: '', to_name: '' })}
                     data-testid="at-send-query" className="w-full px-2 py-2 rounded bg-slate-800 border border-slate-700 text-sm" />
              {suggestions.length > 0 && (
                <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-slate-800 border border-emerald-400/40 rounded-lg max-h-56 overflow-y-auto" data-testid="at-send-suggestions">
                  {suggestions.map((r) => (
                    <button key={r.id} onClick={() => pickRecipient(r)} data-testid={`at-suggest-${r.username}`} className="w-full text-left px-2 py-1.5 hover:bg-slate-700 text-xs border-b border-slate-700 last:border-0">
                      <div className="font-black">{r.name || r.username}</div>
                      <div className="text-[10px] opacity-70">@{r.username} {r.mobile ? `· ${r.mobile}` : ''}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {sendForm.to_user_id && (
              <div className="rounded bg-emerald-500/15 border border-emerald-400/30 p-2 text-xs">
                ✓ Recipient: <b>{sendForm.to_name}</b> <span className="opacity-80">@{sendForm.to_username}</span>
              </div>
            )}
            <input type="number" placeholder="Amount (AT)" value={sendForm.amount} onChange={(e) => setSendForm({ ...sendForm, amount: e.target.value })} data-testid="at-send-amount" className="w-full px-2 py-2 rounded bg-slate-800 border border-slate-700 text-sm" />
            <input placeholder="Note (optional)" value={sendForm.note} onChange={(e) => setSendForm({ ...sendForm, note: e.target.value })} className="w-full px-2 py-2 rounded bg-slate-800 border border-slate-700 text-sm" />
            <button onClick={send} disabled={busy || !sendForm.to_user_id} data-testid="at-send-submit" className="w-full px-3 py-2 rounded bg-emerald-500 text-black text-sm font-black disabled:opacity-40">{busy ? '⏳' : '✓ Send AT'}</button>
            <div className="text-[10px] opacity-70 text-center">Instant · 0 fee · search by name, @username, or mobile</div>
          </div>
        )}

        {action === 'scan' && (
          <div className="space-y-2">
            {!scanning && !scannedPayee && (
              <button onClick={startScan} data-testid="at-scan-start" className="w-full px-3 py-3 rounded-xl bg-fuchsia-500 text-white text-sm font-black">📷 Open Camera & Scan QR</button>
            )}
            {scanning && (
              <div className="space-y-2">
                <video ref={videoRefLocal} autoPlay muted playsInline onLoadedMetadata={scanLoop} onCanPlay={scanLoop} className="w-full rounded-lg bg-black aspect-square object-cover" data-testid="at-scan-video" />
                <button onClick={stopScan} className="w-full px-3 py-2 rounded bg-red-500 text-white text-xs font-black">⏹ Stop</button>
              </div>
            )}
            {scannedPayee && (
              <div className="space-y-2" data-testid="at-scan-confirm">
                <div className="rounded bg-emerald-500/15 border border-emerald-400/30 p-3 text-center">
                  <div className="text-lg font-black">@{scannedPayee.to_username}</div>
                  {scannedPayee.name && <div className="text-xs opacity-80">{scannedPayee.name}</div>}
                </div>
                <input type="number" placeholder="Amount (AT)" value={sendForm.amount} onChange={(e) => setSendForm({ ...sendForm, amount: e.target.value })} data-testid="at-scan-amount" className="w-full px-2 py-2 rounded bg-slate-800 border border-slate-700 text-sm" />
                <input placeholder="Note (optional)" value={sendForm.note} onChange={(e) => setSendForm({ ...sendForm, note: e.target.value })} className="w-full px-2 py-2 rounded bg-slate-800 border border-slate-700 text-sm" />
                <button onClick={payScanned} disabled={busy} data-testid="at-scan-pay" className="w-full px-3 py-2 rounded bg-fuchsia-500 text-white text-sm font-black disabled:opacity-40">{busy ? '⏳' : `Pay ${sendForm.amount || '—'} AT`}</button>
                <button onClick={() => { setScannedPayee(null); setSendForm({ ...sendForm, amount: '', note: '' }); }} className="w-full px-2 py-1 rounded bg-slate-700 text-slate-200 text-[11px]">↺ Scan Again</button>
              </div>
            )}
          </div>
        )}

        {action === 'qr' && qr && (
          <div className="text-center space-y-2" data-testid="at-my-qr">
            <img src={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(qr.payload)}`} alt="AT Wallet QR" className="w-64 h-64 mx-auto rounded-xl bg-white p-2" />
            <div className="text-xs opacity-80">Anyone with the Anant app can scan this to send you AT</div>
            <button onClick={() => { navigator.clipboard.writeText(qr.text); toast.success('Copied'); }} className="w-full px-3 py-2 rounded bg-slate-700 text-white text-xs font-black">📋 Copy Deep Link</button>
          </div>
        )}

        {action === 'deposit' && (
          <div className="space-y-2">
            <p className="text-xs opacity-80">Buy AT with INR via UPI/QR/Bank at 1:1 rate.</p>
            <button onClick={goDeposit} data-testid="at-deposit-go" className="w-full px-3 py-2 rounded bg-cyan-500 text-black text-sm font-black">Go to Deposit Portal →</button>
          </div>
        )}
        {action === 'withdraw' && (
          <div className="space-y-2">
            <input type="number" placeholder="Amount (AT)" value={withdrawForm.amount} onChange={(e) => setWithdrawForm({ ...withdrawForm, amount: e.target.value })} data-testid="at-wd-amount" className="w-full px-2 py-2 rounded bg-slate-800 border border-slate-700 text-sm" />
            <select value={withdrawForm.method} onChange={(e) => setWithdrawForm({ ...withdrawForm, method: e.target.value })} data-testid="at-wd-method" className="w-full px-2 py-2 rounded bg-slate-800 border border-slate-700 text-sm">
              <option value="upi">UPI</option><option value="bank">Bank Transfer</option><option value="cash">Cash Counter</option>
            </select>
            {withdrawForm.method === 'upi' && <input placeholder="UPI ID" value={withdrawForm.upi_id} onChange={(e) => setWithdrawForm({ ...withdrawForm, upi_id: e.target.value })} data-testid="at-wd-upi" className="w-full px-2 py-2 rounded bg-slate-800 border border-slate-700 text-sm" />}
            {withdrawForm.method === 'bank' && (
              <>
                <input placeholder="Account No" value={withdrawForm.bank_account} onChange={(e) => setWithdrawForm({ ...withdrawForm, bank_account: e.target.value })} data-testid="at-wd-acc" className="w-full px-2 py-2 rounded bg-slate-800 border border-slate-700 text-sm" />
                <input placeholder="IFSC" value={withdrawForm.bank_ifsc} onChange={(e) => setWithdrawForm({ ...withdrawForm, bank_ifsc: e.target.value })} data-testid="at-wd-ifsc" className="w-full px-2 py-2 rounded bg-slate-800 border border-slate-700 text-sm" />
                <input placeholder="Bank Name" value={withdrawForm.bank_name} onChange={(e) => setWithdrawForm({ ...withdrawForm, bank_name: e.target.value })} className="w-full px-2 py-2 rounded bg-slate-800 border border-slate-700 text-sm" />
              </>
            )}
            <button onClick={withdraw} disabled={busy} data-testid="at-wd-submit" className="w-full px-3 py-2 rounded bg-amber-500 text-black text-sm font-black disabled:opacity-40">{busy ? '⏳' : '💸 Submit Request'}</button>
            <div className="text-[10px] opacity-70 text-center">Amount held immediately · admin approves & pays within 24h · rejected → auto-refund</div>
          </div>
        )}
        {action === 'ledger' && (
          <div className="max-h-96 overflow-y-auto">
            {ledger.length === 0 && <div className="text-center py-8 opacity-60 text-xs">No AT transactions yet</div>}
            {ledger.map((tx) => (
              <div key={tx.id} className="flex items-center gap-2 py-1.5 border-b border-white/5 text-[11px]" data-testid={`at-ledger-${tx.id}`}>
                <div className={`w-1 self-stretch rounded ${tx.direction === 'credit' ? 'bg-emerald-400' : 'bg-red-400'}`} />
                <div className="flex-1 min-w-0">
                  <div className="truncate">{tx.description || tx.tx_type}</div>
                  <div className="text-[9px] opacity-60">{tx.tx_type} · {fmtDateTime(tx.created_at)}</div>
                </div>
                <div className={`font-black ${tx.direction === 'credit' ? 'text-emerald-300' : 'text-red-300'}`}>{tx.direction === 'credit' ? '+' : '−'}{tx.amount}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Wallets;
